import { ChevronDown, LogOut, RefreshCcw } from "lucide-react";
import { NavLink } from "react-router-dom";
import type React from "react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth";
import { useOwnerContext } from "@/lib/owner-context";
import { cn } from "@/lib/utils";

interface AppHeaderProps {
  title: string;
  showOwnerSwitcher?: boolean;
}

/** FSD p65's agent menu, plus the two screens p58/p61 name.
 *
 * Until this existed the app had no persistent navigation at all --
 * pages linked to each other ad hoc, so a screen nobody happened to
 * link to was unreachable even though its route worked. p64 asks for
 * sidebar navigation; this is the same menu in a header, which suits
 * four-to-seven items better than a sidebar does.
 */
const NAV = [
  { to: "/", label: "Dashboard", end: true },
  { to: "/owners", label: "Owners", end: false },
  { to: "/my-loads", label: "My Loads", end: false },
  { to: "/post-load", label: "Post Load", end: false },
  { to: "/marketplace", label: "Marketplace", end: false },
  { to: "/trips", label: "Trips", end: false },
  { to: "/payments", label: "Payments", end: false },
  { to: "/reports", label: "Reports", end: false },
  { to: "/profile", label: "Profile", end: false },
] as const;

export function AppHeader({ title, showOwnerSwitcher = true }: AppHeaderProps): React.JSX.Element {
  const { user, logout } = useAuth();
  const { owners, currentOwner, setOwner, refresh, isLoading } = useOwnerContext();
  const [menuOpen, setMenuOpen] = useState(false);

  const activeOwners = owners.filter((o) => o.status === "active");

  return (
    <header className="border-b bg-background">
      <div className="mx-auto flex max-w-5xl items-center justify-between px-6 py-4">
        <div>
          <p className="text-xs uppercase tracking-widest text-muted-foreground">
            VandieZ Agent
          </p>
          {/* A real <h1>, not a styled <p>: every page should have
              exactly one, and it lets a test (or a screen reader)
              distinguish the page TITLE from the nav link that now
              carries the same words on every screen. */}
          <h1 className="text-lg font-semibold">{title}</h1>
        </div>
        <div className="flex items-center gap-3">
          {showOwnerSwitcher && currentOwner !== null ? (
            <div className="relative">
              <Button
                variant="outline"
                size="sm"
                onClick={() => { setMenuOpen((b) => !b); }}
              >
                Acting as agent for{" "}
                <span className="font-semibold">{currentOwner.owner_full_name}</span>
                <ChevronDown className="h-3 w-3" />
              </Button>
              {menuOpen ? (
                <div className="absolute right-0 z-10 mt-2 min-w-[16rem] rounded-md border bg-background p-1 shadow-lg">
                  {activeOwners.map((o) => (
                    <button
                      key={o.owner_id}
                      type="button"
                      className={`block w-full rounded-sm px-3 py-2 text-left text-sm hover:bg-muted ${
                        o.owner_id === currentOwner.owner_id ? "bg-muted font-semibold" : ""
                      }`}
                      onClick={() => {
                        setOwner(o.owner_id);
                        setMenuOpen(false);
                      }}
                    >
                      <div>{o.owner_full_name}</div>
                      <div className="text-xs text-muted-foreground">
                        {o.owner_phone_masked}
                      </div>
                    </button>
                  ))}
                  <button
                    type="button"
                    className="mt-1 flex w-full items-center gap-2 rounded-sm px-3 py-2 text-left text-xs text-muted-foreground hover:bg-muted"
                    onClick={() => { void refresh(); setMenuOpen(false); }}
                    disabled={isLoading}
                  >
                    <RefreshCcw className="h-3 w-3" />
                    Refresh list
                  </button>
                </div>
              ) : null}
            </div>
          ) : null}
          <div className="text-right text-sm">
            <p className="font-medium">{user?.full_name ?? "—"}</p>
            <p className="text-muted-foreground">{user?.phone ?? ""}</p>
          </div>
          <Button variant="outline" size="sm" onClick={() => void logout()}>
            <LogOut className="h-4 w-4" />
            Sign out
          </Button>
        </div>
      </div>

      <nav className="mx-auto flex max-w-5xl flex-wrap gap-1 px-6 pb-3">
        {NAV.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={({ isActive }) =>
              cn(
                "rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
                isActive
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:bg-muted",
              )
            }
          >
            {item.label}
          </NavLink>
        ))}
      </nav>
    </header>
  );
}
